import { useEffect } from "react";

import clsx from "clsx";
import { useLocalStorage } from "usehooks-ts";

import { usePageTitle } from "@/atoms/page-title-atom";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { ClicksAndViewsEnhancedModellingBanner } from "@features/clicks-and-view-enhanced-modelling/clicks-and-views-enhanced-modelling";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { QuickFiltersProvider } from "@features/quick-filters";
import {
  ProfitabilityGauge,
  SalesChartContainer,
  SalesV4Provider,
  TableContainer,
} from "@features/sales-v4";
import { PageFilters } from "@features/sales-v4/page-filters";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { FeedbackForm } from "@shared/feedback-form";

const FEEDBACK_FORM_ID = "92477925-4592-49dd-8af7-14f042f73874";

export const SalesV4 = () => {
  const [, setPageTitle] = usePageTitle();
  const { loading: breakdownsLoading } = useSalesBreakdownConfigs();
  const [showProfitabilityGaugeInfo, setShowProfitabilityGaugeInfo] =
    useLocalStorage("showProfitabilityGaugeInfo", true);

  const { user } = useUser();

  useEffect(() => {
    setPageTitle("Sales Attribution");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.DASHBOARD_V2_SALES,
      user,
    });
  }, [user]);

  return (
    <div
      className={clsx("bg-v2-primary w-full", {
        "h-[calc(100%-64px)]": breakdownsLoading,
        "min-h-[calc(100%-64px)]": !breakdownsLoading,
      })}
    >
      <CustomMetricsProvider>
        <QuickFiltersProvider target="revenue">
          {breakdownsLoading ? (
            <LoadingSlide />
          ) : (
            <SalesV4Provider>
              <div className="overflow-auto">
                <ClicksAndViewsEnhancedModellingBanner className="rounded-md mx-2 mt-2" />
              </div>
              <div className="flex flex-col">
                <PageFilters
                  showProfitabilityGaugeInfo={showProfitabilityGaugeInfo}
                  setShowProfitabilityGaugeInfo={setShowProfitabilityGaugeInfo}
                />
                <div className="flex gap-4 px-6 mb-6">
                  <SalesChartContainer />
                  <div
                    className={clsx(
                      "shrink-0 w-[270px]",
                      !showProfitabilityGaugeInfo && "hidden",
                    )}
                  >
                    <ProfitabilityGauge />
                  </div>
                </div>
                <TableContainer />
              </div>
            </SalesV4Provider>
          )}
        </QuickFiltersProvider>
      </CustomMetricsProvider>
      <FeedbackForm formId={FEEDBACK_FORM_ID} />
    </div>
  );
};
